import { useCallback, useMemo } from "react"
import { Link } from "react-router-dom"
import { chain, get, isFunction, map, noop, every, isEmpty, filter, isNil, isNaN, concat, orderBy } from "lodash"
import { filterFavorite } from "modules/resources/components/filter-form/reducer"

function makeIsEnabled(form) {
  if (form && form.isEnabled && isFunction(form.isEnabled)) {
    return form.isEnabled
  }
  return noop
}

export default function useRows(columns, data, idField, sortValueGetter, direction, editForm, deleteForm, filters, favoritesScreen) {
  const getPermissions = useCallback((item) => ({
    editable: makeIsEnabled(editForm)(item),
    deletable: makeIsEnabled(deleteForm)(item),
  }), [deleteForm, editForm])

  // On favorites screen, filter form will omit "favorite" field,
  // and we will still apply the filter logic.
  if (favoritesScreen) {
    filters = [...filters, filterFavorite]
  }

  return useMemo(() => chain(data)
    // We use thru instead of a filter to avoid iterating through all the rows if there are not filters
    .thru(rows => isEmpty(filters) ? rows : filter(rows, row => every(filters, filter => filter(row))))
    // We want Nil values to be at the end of the list regardless of the sort
    .partition((rowObject) => {
      const value = sortValueGetter(rowObject)
      return isNil(value) || isNaN(value)
    })
    .thru(([nils, values]) => concat(
      orderBy(values, sortValueGetter, [direction]),
      orderBy(nils, idField, direction ?? "asc"),
    ))
    .map((datum, index) => {
      return {
        key: get(datum, idField, `row-${index}`),
        columns: map(columns, c => {
          if (c.render) {
            return c.render(datum)
          }

          let value = get(datum, c.id)
          if (c.formatter) {
            value = c.formatter(value)
          }

          if (c.linkTo && c.linkTo(datum)) {
            // [TODO] [#184018795] JSX shouldn't be in selectors or models
            value = <Link to={c.linkTo(datum)}>{value}</Link>
          }

          return value
        }),
        ...getPermissions(datum),

      }
    })
    .value(), [data, sortValueGetter, direction, filters, idField, columns, getPermissions])
}
